import {Loader, Modal, Notification} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {addDays} from 'date-fns';
import Head from 'next/head';
import Image from 'next/image';
import {useRouter} from 'next/router';
import {parseCookies, setCookie} from 'nookies';
import {useCallback, useEffect, useRef, useState} from 'react';
import {MdOutlineTipsAndUpdates} from 'react-icons/md';
import type {z} from 'zod';
import AI_ES_BUILDER_LOGO from '../../../../public/next-image/aiEsBuilder/logo.png';
import {TITLE_SUFFIX} from '../../../config/constants';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {AI_ES_BUILDER_OGP} from '../../../config/constants/img';
import type {HashtagCategoryType} from '../../../core/entities/entrysheetBuilder/constants';
import type {GenerateResponseSchema} from '../../../core/entities/entrysheetBuilder/schema';
import type {GetHashtagType} from '../../../core/entities/register/schema';
import {useGenerateEntrySheet} from '../../../core/repositories/entrySheetBuilderRepository';
import {useAuth} from '../../../core/usecases/auth/AuthContext';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {cn} from '../../../lib/classname';
import {NOT_SHOW_ADD_TO_PROFILE_MODAL_BUTTON_OPTION_ID} from '../constants';
import {useHandleQueryAnswers} from '../usecase/useHandleQueryAnswers';
import {AiEsBuilderChat} from './Container/AiEsBuilderChat';
import {SelectHashtagModal} from './Container/Presenter/SelectHashTagModal';
import {GeneratedSystemMessage} from './Container/Presenter/SystemMessage';
import {GenerateTips} from './Container/Tips';
import {TmpResult} from './Container/TmpResult';

export const GeneratePage = () => {
  const cookies = parseCookies();
  const {isValid} = validateCookie(cookies);
  const router = useRouter();
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath)}`;
  const TITLE = 'AI ESビルダー';
  const {status} = useAuth();
  const {questionAnswers, themeId, setThemeId} = useHandleQueryAnswers();
  const generate = useGenerateEntrySheet({});

  const [spTipsOpen, {open: openSpTips, close: closeSpTips}] = useDisclosure();

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    questionAnswers.map(() => ''),
  );
  const [selectedHashtags, setSelectedHashtags] = useState<GetHashtagType[]>(
    [],
  );
  const [hashtagCategory, setHashtagCategory] = useState<
    HashtagCategoryType | undefined
  >();

  const [closeNotification, setCloseNotification] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const childEditingStates = useRef(new Set<number>());
  const updateEditingState = (index: number, isEditing: boolean) => {
    if (isEditing) {
      childEditingStates.current.add(index);
    } else {
      childEditingStates.current.delete(index);
    }
  };

  if (generate.data?.ok && !isValid) {
    // 会員登録後の連携用クッキー
    const data = generate.data.value as z.infer<typeof GenerateResponseSchema>;
    setCookie(null, 'entrysheetBuilderId', data.entrysheetBuilderId, {
      path: '/',
      expires: addDays(new Date(), 1),
    });
  }

  const handleSelectOption = (selectContent: string, index?: number) => {
    if (index === undefined) {
      setCurrentStepIndex((prev) => prev + 1);
    }

    setSelectedOptions((prev) => {
      const newSelectedOptions = [...prev];
      newSelectedOptions[index === undefined ? currentStepIndex : index] =
        selectContent;
      return newSelectedOptions;
    });
  };

  const handleSelectHashtag = (index?: number) => {
    if (index === undefined) {
      setCurrentStepIndex((prev) => prev + 1);
    }

    setSelectedOptions((prev) => {
      const newSelectedOptions = [...prev];
      newSelectedOptions[index === undefined ? currentStepIndex : index] =
        selectedHashtags.map((tag) => tag.name).join(', ');
      return newSelectedOptions;
    });
  };

  const cancel = (hashtagId: number) => {
    setSelectedHashtags((prev) => prev.filter((tag) => tag.id !== hashtagId));
  };

  const handleGenerate = useCallback(() => {
    const QAPrompt = selectedOptions
      .map((option, index) => {
        return `Q: ${questionAnswers[index]?.question ?? ''}\nA:${option}\n`;
      })
      .join('\n');

    const hashtagIds = selectedHashtags
      .filter((tag) => tag.id !== 0)
      .map((tag) => tag.id);

    generate.mutate({
      prompt: QAPrompt,
      theme: selectedOptions[0] ?? '',
      themeId,
      hashtagIds,
    });
  }, [generate, questionAnswers, selectedOptions, selectedHashtags, themeId]);

  // TODO 副作用を使用しないように修正.
  // biome-ignore lint/correctness/useExhaustiveDependencies: 依存にhandleGenerateを含めたら無限ループが始まる危険あり
  useEffect(() => {
    // 全ての回答が完了したら生成
    if (
      questionAnswers.length > 1 &&
      questionAnswers.length === currentStepIndex
    ) {
      handleGenerate();
    }
  }, [questionAnswers, currentStepIndex]);

  // 未ログインでGenerateが成功したらpathパラメータを付与
  // biome-ignore lint/correctness/useExhaustiveDependencies: 言うとおりにしたらBugsnagへの無限リクエストが走ってしまう
  useEffect(() => {
    if (generate.data?.ok && !isValid) {
      const data = generate.data.value as z.infer<
        typeof GenerateResponseSchema
      >;
      void router.push(
        `/engineer/ai-es-builder/${data.entrysheetBuilderId}`,
        undefined,
        {shallow: true, scroll: false},
      );
    }
  }, [generate.data]);

  // テーマへの回答後ResizeObserverを登録
  useEffect(() => {
    if (!themeId) return;

    const observer = new ResizeObserver(() => {
      // 子コンポーネントが編集中の場合はスクロールしない
      if (childEditingStates.current.size > 0) return;
      bottomRef.current?.scrollIntoView({behavior: 'smooth'});
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [themeId]);

  const isShowAddToProfileButton = () =>
    questionAnswers[0]?.options
      ?.filter(
        (option) =>
          option.id === NOT_SHOW_ADD_TO_PROFILE_MODAL_BUTTON_OPTION_ID,
      )
      .map((option) => option.choice)
      .filter((choice) => choice === selectedOptions[0]).length === 0;

  return (
    <>
      <Head>
        <title key="title">
          {TITLE}
          {TITLE_SUFFIX}
        </title>
        <meta
          key="og:title"
          property="og:title"
          content={TITLE + TITLE_SUFFIX}
        />
        <meta
          key="og:site_name"
          property="og:site_name"
          content={TITLE + TITLE_SUFFIX}
        />
        <meta key="og:url" property="og:url" content={currentUrl} />
        <meta
          key="og:image"
          property="og:image"
          content={`${GSSKT_HOST}${AI_ES_BUILDER_OGP}`}
        />
        <meta
          key="description"
          name="description"
          content="エンジニア企業の求人情報、選考情報・先輩のエントリーシート・選考突破法、コーディング試験対策などの多様なイベント、エンジニア同士が集まる専用コミュニティなどを通して就活を支援します。"
        />
        <meta
          key="keywords"
          name="keywords"
          content="外資,就活,新卒,エンジニア,技術職,エンジニア就活サイト,外資IT企業,新卒エンジニア外資,外資系IT新卒採用,テクノロジー外資就職,外資系ソフトウェア開発,プログラミング求人外資,外資系エンジニアキャリア,外資系開発職就活,グローバルIT企業求人,外資系技術職,システムエンジニア,ソフトウェアエンジニア求人,フロントエンドエンジニア,バックエンドエンジニア,AIエンジニア,データサイエンティスト,セキュリティエンジニア,エンジニアコミュニティ"
        />
        <link rel="canonical" href={currentUrl} key="canonical" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0,maximum-scale=1.0"
        />
      </Head>

      <div
        className={cn(
          'relative w-full pc:w-[1200px]',
          status !== 'authenticated' && 'pt-[70px]',
        )}
      >
        <div className="flex flex-col mx-16 pc:mx-0 bg-white p-16 shadow-card rounded-16 text-black-1 gap-8">
          <h1>
            <Image
              src={AI_ES_BUILDER_LOGO}
              alt="AI ESビルダー"
              className="w-[256px]"
            />
          </h1>
          <div className="text-base">
            エンジニア就活をAIがサポート。あなたのスキルや経験を引き立てるエントリーシートをビルドしよう。
            <br />
            生成した内容をプロフィールの開発経験欄に登録することができるようになりました。
          </div>
        </div>
      </div>
      <div className="flex w-full pc:w-[1200px] gap-16 mt-16">
        <div
          ref={containerRef}
          className="bg-white w-full px-16 py-24 overflow-y-auto rounded-16 pc:w-[762px] mx-auto"
        >
          <TmpResult
            options={questionAnswers[0]?.options ?? []}
            isValid={isValid}
          />

          <div>
            {questionAnswers.slice(0, currentStepIndex + 1).map((qa, index) => {
              return (
                <AiEsBuilderChat
                  key={qa.id}
                  index={index}
                  qaId={qa.id}
                  qaInputType={qa.inputType}
                  options={qa.options ?? []}
                  qaQuestion={qa.question}
                  selectedOptions={selectedOptions}
                  setThemeId={setThemeId}
                  handleSelectOption={handleSelectOption}
                  selectedHashtags={selectedHashtags}
                  setSelectedHashtags={setSelectedHashtags}
                  setHashtagCategory={setHashtagCategory}
                  handleSelectHashtag={handleSelectHashtag}
                  cancel={cancel}
                  updateEditingState={updateEditingState}
                />
              );
            })}
            {generate.data?.ok &&
              (() => {
                const data = generate.data.value as z.infer<
                  typeof GenerateResponseSchema
                >;
                return (
                  <GeneratedSystemMessage
                    isValid={isValid}
                    themeId={themeId}
                    message={data.generatedText}
                    isModeration={data.entrysheetBuilderId !== ''}
                    rerunClick={handleGenerate}
                    isShowAddToProfileButton={isShowAddToProfileButton()}
                    isOpenModal={true}
                    selectedHashtagIds={selectedHashtags.map((tag) => tag.id)}
                  />
                );
              })()}
            {generate.isLoading && (
              <div className="w-full flex justify-center mt-16">
                <Loader
                  variant="dots"
                  size="lg"
                  className="fill-gradient-ai-l"
                />
              </div>
            )}
            <SelectHashtagModal
              key={hashtagCategory?.key}
              hashtagCategory={hashtagCategory}
              onClose={() => {
                setHashtagCategory(undefined);
              }}
              handleSelectHashtag={(hashtag: GetHashtagType) => {
                setSelectedHashtags(
                  (prev: GetHashtagType[]): GetHashtagType[] =>
                    [...prev, hashtag].filter(
                      (item, index, self): item is GetHashtagType =>
                        index ===
                        self.findIndex((other) => other.id === item.id),
                    ),
                );
                setHashtagCategory(undefined);
              }}
            />
          </div>
        </div>
        <div className="sticky top-[80px] self-start hidden pc:block">
          <GenerateTips />
        </div>
        <button
          type="button"
          id="ai-es-builder-sp-tips"
          onClick={openSpTips}
          className="fixed cursor-pointe pc:hidden top-[220px] right-0 m-8 p-16 bg-white shadow-card rounded-full"
        >
          <MdOutlineTipsAndUpdates size={24} />
        </button>

        <Modal opened={spTipsOpen} onClose={closeSpTips} className="pc:hidden">
          <GenerateTips />
        </Modal>
      </div>
      {!closeNotification && (
        <Notification
          title="開発経験を入力することで、エンジニア向けスカウトが5倍届きやすくなります。"
          className="fixed py-16 bottom-0 pc:bottom-auto pc:top-[100px] pc:right-8"
          onClose={() => setCloseNotification(true)}
        />
      )}
      <div ref={bottomRef} />
    </>
  );
};
